import { z } from "zod";

/**
 * Session-storage primitives, kept free of any notion of *what* is being stored.
 * Every function is a no-op (or `undefined`) without a `window`, so callers can
 * run them unguarded during SSR or in a non-DOM test environment.
 */

/**
 * Read and validate a stored JSON value. Parsed through the schema so a corrupt,
 * hand-edited or outdated entry degrades to "nothing stored" rather than
 * propagating a malformed value into application state.
 */
export const readSessionValue = <T>(
  key: string,
  schema: z.ZodSchema<T>,
): T | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? schema.parse(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
};

/** Write a JSON value. Counterpart to {@link readSessionValue}. */
export const writeSessionValue = <T>(key: string, value: T): void => {
  if (typeof window === "undefined") return;

  window.sessionStorage.setItem(key, JSON.stringify(value));
};

/** Drop a single entry. */
export const removeSessionValue = (key: string): void => {
  if (typeof window === "undefined") return;

  window.sessionStorage.removeItem(key);
};

/**
 * Drop every entry whose key starts with `prefix` — how a namespace of related
 * entries is invalidated in one call.
 */
export const clearSessionValuesWithPrefix = (prefix: string): void => {
  if (typeof window === "undefined") return;

  const { sessionStorage } = window;
  const staleKeys: string[] = [];

  // Collected first: removing while iterating by index re-indexes the store.
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(prefix)) staleKeys.push(key);
  }

  staleKeys.forEach((key) => sessionStorage.removeItem(key));
};
